import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Tab, TabGroup, TabList, TabPanels, TabPanel } from '@headlessui/react';
import { ArrowTopRightOnSquareIcon, ArrowPathIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { stringify } from 'query-string';
import { useHistory, useParams } from 'react-router';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import { fetchInfinite } from '@dashboard/services/request';
import Spinner from '@dashboard/components/Spinner';
import CopyElement from '@/dashboard/components/CopyElement';
import { FormElement, Label, TextInput } from '@dashboard/components/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';

const BrowserPreviewWrapper = ({ children, previewUrl, onOpenNewTab, onEdit }) => (
  <div className="h-full">
    <div className="bg-white dark:bg-background border border-gray-100 dark:border-border rounded-lg overflow-hidden h-full shadow">
      <div className="relative flex items-center justify-between bg-white dark:bg-background px-2 py-1 border-b border-gray-100 dark:border-border rounded-t-lg">
        <div className="absolute left-2 top-[7px] flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
        </div>

        <p className="text-gray-400 dark:text-muted-foreground text-xs mx-auto w-2/3 truncate text-center">
          {previewUrl || '\u00A0'}
        </p>

        <div className="absolute right-2 flex items-center gap-1">
          {onEdit && (
            <button
              onClick={onEdit}
              className="p-1 hover:bg-gray-100 dark:hover:bg-muted rounded"
              title="Edit page"
            >
              <PencilIcon className="w-4 h-4 text-gray-500 dark:text-muted-foreground" />
            </button>
          )}
          <button
            onClick={onOpenNewTab}
            className="p-1 hover:bg-gray-100 dark:hover:bg-muted rounded"
            title="Open in new tab"
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4 text-gray-500 dark:text-muted-foreground" />
          </button>
        </div>
      </div>

      <div className="h-[calc(100%-2rem)]">{children}</div>
    </div>
  </div>
);

function getEmailPreviewSrc(mailTemplatesUrl, page) {
  switch (page.type) {
    case 'form':
      return `${mailTemplatesUrl}/submission-confirmation-email/${page._id}`;

    case 'checkout':
      // @TODO (M14)
      if (page.product?.type === 'charge') {
        return `${mailTemplatesUrl}/payment-confirmation-email/${page._id}`;
      }

      if (page.product?.type === 'subscription') {
        return `${mailTemplatesUrl}/subscription-confirmation-email/${page._id}`;
      }

      break;
    case 'event':
      return `${mailTemplatesUrl}/event-confirmation-email/${page._id}`;
    default:
      return ``;
  }
}

export default function PageCompare() {
  const { token } = useAuth();
  const history = useHistory();
  const { pageId } = useParams();
  const query = useUrlQuery();
  const { search, searchInput, setSearchInput } = useSearchInput(query.search);

  const pageType = query.pageType || '';
  const usageType = query.usageType || '';
  const hostedLayoutType = query.hostedLayoutType || '';
  const siteLayoutType = query.siteLayoutType || '';
  const hasCustomCss = query.hasCustomCss || '';
  const visitCount = query.visitCount || '';
  const tab = parseInt(query.tab, 10) || 0;

  const [refreshKey, setRefreshKey] = useState(0);
  const [isLoadingNewPage, setIsLoadingNewPage] = useState(false);

  const buildQueryString = (overrides = {}) => {
    const params = {
      ...(pageType && { pageType }),
      ...(usageType && { usageType }),
      ...(hostedLayoutType && { hostedLayoutType }),
      ...(siteLayoutType && { siteLayoutType }),
      ...(hasCustomCss && { hasCustomCss }),
      ...(visitCount && { visitCount }),
      ...(tab && { tab }),
      ...(search && { search }),
      ...overrides,
    };
    // Remove empty values
    Object.keys(params).forEach(key => {
      if (!params[key]) delete params[key];
    });
    const qs = stringify(params);
    return qs ? `?${qs}` : '';
  };

  const setFilter = (key, value) => {
    const overrides = { [key]: value };
    // Clear sub-filters when parent changes
    if (key === 'usageType') {
      if (value === 'hosted') overrides.siteLayoutType = '';
      if (value === 'site') overrides.hostedLayoutType = '';
      if (!value) {
        overrides.hostedLayoutType = '';
        overrides.siteLayoutType = '';
      }
    }
    const basePath = pageId ? `/admin/pages/compare/${pageId}` : '/admin/pages/compare';
    history.replace(`${basePath}${buildQueryString(overrides)}`);
  };

  useEffect(() => {
    if (pageId) {
      setRefreshKey(prev => prev + 1);
    }
  }, [pageId]);

  const apiParams = {
    search,
    ...(pageType && { type: pageType }),
    ...(usageType && { usageType }),
    ...(hostedLayoutType && { hostedLayoutType }),
    ...(siteLayoutType && { siteLayoutType }),
    ...(hasCustomCss && { hasCustomCss }),
    ...(visitCount && { visitCount }),
  };

  const { data, isError, isLoading } = useQuery({
    queryKey: pageId
      ? ['pages-compare-by-id', pageId, refreshKey]
      : [
          'pages-compare-random',
          search,
          pageType,
          usageType,
          hostedLayoutType,
          siteLayoutType,
          hasCustomCss,
          visitCount,
          refreshKey,
        ],
    queryFn: async () => {
      if (pageId) {
        return fetchInfinite(`/admin/pages/${pageId}`, token);
      }

      const response = await fetchInfinite(`/admin/pages/random?${stringify(apiParams)}`, token);

      const newPage = response?.data?.[0];
      if (newPage?._id) {
        history.replace(`/admin/pages/compare/${newPage._id}${buildQueryString()}`);
      }

      return response;
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const pages = data?.data || [];
  const currentPage = pages[0];
  const total = data?.total || 0;

  const handleLoadNewPage = async () => {
    try {
      setIsLoadingNewPage(true);
      const response = await fetchInfinite(`/admin/pages/random?${stringify(apiParams)}`, token);

      const newPage = response?.data?.[0];
      if (newPage?._id) {
        history.push(`/admin/pages/compare/${newPage._id}${buildQueryString()}`);
      }
    } catch (error) {
      console.error('Failed to load new page:', error);
    } finally {
      setIsLoadingNewPage(false);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  if (isLoading) {
    return (
      <div className="p-4 flex justify-center">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return <div className="p-4">Failed to load pages. Please try again.</div>;
  }

  if (!pages.length) {
    return <div className="p-4">No pages found</div>;
  }

  const localBaseUrl = 'https://checkoutpage.dev';
  const prodBaseUrl = 'https://checkoutpage.com';
  const localEmailBaseUrl = 'https://api.checkoutpage.dev/mail-templates';
  const prodEmailBaseUrl = 'https://api.checkoutpage.com/mail-templates';

  const localCheckoutPreviewUrl =
    currentPage?.seller?._id && currentPage?._id
      ? `${localBaseUrl}/c/${currentPage.seller.name || currentPage.seller._id}/${currentPage.slug || currentPage._id}?testing=true&preview=true`
      : '';

  const prodCheckoutPreviewUrl =
    currentPage?.seller?._id && currentPage?._id
      ? `${prodBaseUrl}/c/${currentPage.seller.name || currentPage.seller._id}/${currentPage.slug || currentPage._id}?testing=true&preview=true`
      : '';

  const localEmbedPreviewUrl =
    currentPage?.seller?._id && currentPage?._id
      ? `${localBaseUrl}/embed/${currentPage.seller.name || currentPage.seller._id}/${currentPage.slug || currentPage._id}?testing=true&preview=true`
      : '';

  const prodEmbedPreviewUrl =
    currentPage?.seller?._id && currentPage?._id
      ? `${prodBaseUrl}/embed/${currentPage.seller.name || currentPage.seller._id}/${currentPage.slug || currentPage._id}?testing=true&preview=true`
      : '';

  const localPopupPreviewUrl =
    currentPage?.seller?._id && currentPage?._id
      ? `${localBaseUrl}/overlay/${currentPage.seller.name || currentPage.seller._id}/${currentPage.slug || currentPage._id}?testing=true&preview=true`
      : '';

  const prodPopupPreviewUrl =
    currentPage?.seller?._id && currentPage?._id
      ? `${prodBaseUrl}/overlay/${currentPage.seller.name || currentPage.seller._id}/${currentPage.slug || currentPage._id}?testing=true&preview=true`
      : '';

  const localConfirmationPreviewUrl =
    currentPage?.seller?._id && currentPage?._id
      ? `${localBaseUrl}/c/${currentPage.seller.name || currentPage.seller._id}/${
          currentPage.slug || currentPage._id
        }/confirmation/${Math.random()}?testing=true&preview=true`
      : '';

  const prodConfirmationPreviewUrl =
    currentPage?.seller?._id && currentPage?._id
      ? `${prodBaseUrl}/c/${currentPage.seller.name || currentPage.seller._id}/${
          currentPage.slug || currentPage._id
        }/confirmation/${Math.random()}?testing=true&preview=true`
      : '';

  const localEmailPreviewUrl =
    currentPage?._id && getEmailPreviewSrc(localEmailBaseUrl, currentPage);

  const prodEmailPreviewUrl = currentPage?._id && getEmailPreviewSrc(prodEmailBaseUrl, currentPage);

  const handleEditPage = environment => {
    if (currentPage?._id) {
      const path = `/pages/${currentPage._id}/customize`;
      const url =
        environment === 'production' ? `https://checkoutpage.com/dashboard${path}` : `${path}`;
      window.open(url, '_blank');
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600 dark:text-muted-foreground">
              <span>Total pages: {total}</span>

              {currentPage && (
                <span className="ml-2 font-medium">
                  ({currentPage.name || 'Unnamed Page'} &middot; {currentPage.visitCount ?? 0}{' '}
                  visits)
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleLoadNewPage}
                className="p-2 rounded hover:bg-gray-100 dark:hover:bg-muted flex items-center gap-2"
                title="Load random page"
                disabled={isLoading || isLoadingNewPage}
              >
                Load new page{' '}
                <ArrowPathIcon className={clsx('w-4 h-4', isLoadingNewPage && 'animate-spin')} />
              </button>

              <button
                onClick={handleRefresh}
                className="p-2 rounded hover:bg-gray-100 dark:hover:bg-muted flex items-center gap-2"
                title="Refresh current page"
                disabled={isLoading || !pageId}
              >
                Refresh <ArrowPathIcon className={clsx('w-4 h-4', isLoading && 'animate-spin')} />
              </button>
            </div>
          </div>

          <div className="flex items-end gap-x-2">
            <FormElement className="!mb-0">
              <Label htmlFor="pageType">Page type</Label>
              <Select
                name="pageType"
                value={pageType || 'all'}
                onValueChange={value => setFilter('pageType', value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="checkout">Checkout</SelectItem>
                  <SelectItem value="event">Event</SelectItem>
                  <SelectItem value="form">Form</SelectItem>
                </SelectContent>
              </Select>
            </FormElement>

            <FormElement className="!mb-0">
              <Label htmlFor="usageType">Usage type</Label>
              <Select
                name="usageType"
                value={usageType || 'all'}
                onValueChange={value => setFilter('usageType', value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="hosted">Hosted</SelectItem>
                  <SelectItem value="site">Site</SelectItem>
                </SelectContent>
              </Select>
            </FormElement>

            {usageType === 'hosted' && (
              <FormElement className="!mb-0">
                <Label htmlFor="hostedLayoutType">Hosted layout type</Label>
                <Select
                  name="hostedLayoutType"
                  value={hostedLayoutType || 'all'}
                  onValueChange={value =>
                    setFilter('hostedLayoutType', value === 'all' ? '' : value)
                  }
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="double">Double</SelectItem>
                    <SelectItem value="double_banner">Double banner</SelectItem>
                    <SelectItem value="center">Center</SelectItem>
                    <SelectItem value="split">Split</SelectItem>
                  </SelectContent>
                </Select>
              </FormElement>
            )}

            {usageType === 'site' && (
              <FormElement className="!mb-0">
                <Label htmlFor="siteLayoutType">Site layout type</Label>
                <Select
                  name="siteLayoutType"
                  value={siteLayoutType || 'all'}
                  onValueChange={value => setFilter('siteLayoutType', value === 'all' ? '' : value)}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="embed">Embed</SelectItem>
                    <SelectItem value="popup">Pop-up</SelectItem>
                  </SelectContent>
                </Select>
              </FormElement>
            )}

            <FormElement className="!mb-0">
              <Label htmlFor="hasCustomCss">Custom CSS</Label>
              <Select
                name="hasCustomCss"
                value={hasCustomCss || 'all'}
                onValueChange={value => setFilter('hasCustomCss', value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </FormElement>

            <FormElement className="!mb-0">
              <Label htmlFor="visitCount">Visit count</Label>
              <Select
                name="visitCount"
                value={visitCount || 'any'}
                onValueChange={value => setFilter('visitCount', value === 'any' ? '' : value)}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="lt10">&lt; 10</SelectItem>
                  <SelectItem value="10to100">10 - 100</SelectItem>
                  <SelectItem value="gt100">100+</SelectItem>
                </SelectContent>
              </Select>
            </FormElement>

            <FormElement className="!mb-0">
              <Label htmlFor="search">Search</Label>
              <div className="w-64">
                <TextInput
                  border
                  name="search"
                  onChange={e => setSearchInput(e.target.value)}
                  placeholder="Search by page name"
                  value={searchInput}
                />
              </div>
            </FormElement>
          </div>
        </div>
      </div>

      <div className="h-[calc(100vh-300px)]">
        <TabGroup
          className="h-full"
          selectedIndex={tab}
          onChange={index => setFilter('tab', index || '')}
        >
          <TabList className="flex justify-center mb-4">
            <Tab
              selected={currentPage.usageType === 'hosted'}
              className={({ selected }) =>
                clsx(
                  'outline-none group flex items-center space-x-2.5 rounded-md px-4 py-2 text-sm font-medium transition-all duration-300 hover:text-foreground',
                  {
                    'text-foreground bg-muted': selected,
                    'text-muted-foreground bg-transparent': !selected,
                  },
                )
              }
            >
              Hosted checkout
            </Tab>
            <Tab
              selected={currentPage.usageType === 'site' && currentPage.siteLayoutType === 'embed'}
              className={({ selected }) =>
                clsx(
                  'outline-none group flex items-center space-x-2.5 rounded-md px-4 py-2 text-sm font-medium transition-all duration-300 hover:text-foreground',
                  {
                    'text-foreground bg-muted': selected,
                    'text-muted-foreground bg-transparent': !selected,
                  },
                )
              }
            >
              Checkout embed
            </Tab>
            <Tab
              selected={currentPage.usageType === 'site' && currentPage.siteLayoutType === 'popup'}
              className={({ selected }) =>
                clsx(
                  'outline-none group flex items-center space-x-2.5 rounded-md px-4 py-2 text-sm font-medium transition-all duration-300 hover:text-foreground',
                  {
                    'text-foreground bg-muted': selected,
                    'text-muted-foreground bg-transparent': !selected,
                  },
                )
              }
            >
              Pop-up
            </Tab>
            <Tab
              className={({ selected }) =>
                clsx(
                  'outline-none group flex items-center space-x-2.5 rounded-md px-4 py-2 text-sm font-medium transition-all duration-300 hover:text-foreground',
                  {
                    'text-foreground bg-muted': selected,
                    'text-muted-foreground bg-transparent': !selected,
                  },
                )
              }
            >
              Confirmation
            </Tab>
            <Tab
              className={({ selected }) =>
                clsx(
                  'outline-none group flex items-center space-x-2.5 rounded-md px-4 py-2 text-sm font-medium transition-all duration-300 hover:text-foreground',
                  {
                    'text-foreground bg-muted': selected,
                    'text-muted-foreground bg-transparent': !selected,
                  },
                )
              }
            >
              Email
            </Tab>
          </TabList>
          <TabPanels className="h-[calc(100%-3.5rem)]">
            <TabPanel className="h-full">
              <div className="grid grid-cols-2 gap-4 h-full">
                <div>
                  <BrowserPreviewWrapper
                    previewUrl={localCheckoutPreviewUrl}
                    onOpenNewTab={() =>
                      localCheckoutPreviewUrl && window.open(localCheckoutPreviewUrl, '_blank')
                    }
                    onEdit={() => handleEditPage('local')}
                  >
                    {localCheckoutPreviewUrl && (
                      <iframe src={localCheckoutPreviewUrl} className="w-full h-full" />
                    )}
                  </BrowserPreviewWrapper>

                  <div className="mt-4">
                    <div>
                      Seller ID: <CopyElement text={currentPage.seller._id} />
                    </div>
                    <div>
                      Page ID: <CopyElement text={currentPage._id} />
                    </div>
                  </div>
                </div>

                <BrowserPreviewWrapper
                  previewUrl={prodCheckoutPreviewUrl}
                  onOpenNewTab={() =>
                    prodCheckoutPreviewUrl && window.open(prodCheckoutPreviewUrl, '_blank')
                  }
                  onEdit={() => handleEditPage('production')}
                >
                  {prodCheckoutPreviewUrl && (
                    <iframe src={prodCheckoutPreviewUrl} className="w-full h-full" />
                  )}
                </BrowserPreviewWrapper>
              </div>
            </TabPanel>

            <TabPanel className="h-full">
              <div className="grid grid-cols-2 gap-4 h-full">
                <div>
                  <BrowserPreviewWrapper
                    previewUrl={localEmbedPreviewUrl}
                    onOpenNewTab={() =>
                      localEmbedPreviewUrl && window.open(localEmbedPreviewUrl, '_blank')
                    }
                    onEdit={() => handleEditPage('local')}
                  >
                    {localEmbedPreviewUrl && (
                      <iframe src={localEmbedPreviewUrl} className="w-full h-full" />
                    )}
                  </BrowserPreviewWrapper>
                  <div className="mt-4">
                    <div>
                      Seller ID: <code>{currentPage.seller._id}</code>
                    </div>
                    <div>
                      Page ID: <code>{currentPage._id}</code>
                    </div>
                  </div>
                </div>

                <BrowserPreviewWrapper
                  previewUrl={prodEmbedPreviewUrl}
                  onOpenNewTab={() =>
                    prodEmbedPreviewUrl && window.open(prodEmbedPreviewUrl, '_blank')
                  }
                  onEdit={() => handleEditPage('production')}
                >
                  {prodEmbedPreviewUrl && (
                    <iframe src={prodEmbedPreviewUrl} className="w-full h-full" />
                  )}
                </BrowserPreviewWrapper>
              </div>
            </TabPanel>

            <TabPanel className="h-full">
              <div className="grid grid-cols-2 gap-4 h-full">
                <div>
                  <BrowserPreviewWrapper
                    previewUrl={localPopupPreviewUrl}
                    onOpenNewTab={() =>
                      localPopupPreviewUrl && window.open(localPopupPreviewUrl, '_blank')
                    }
                    onEdit={() => handleEditPage('local')}
                  >
                    {localPopupPreviewUrl && (
                      <iframe src={localPopupPreviewUrl} className="w-full h-full" />
                    )}
                  </BrowserPreviewWrapper>

                  <div className="mt-4">
                    <div>
                      Seller ID: <code>{currentPage.seller._id}</code>
                    </div>
                    <div>
                      Page ID: <code>{currentPage._id}</code>
                    </div>
                  </div>
                </div>

                <BrowserPreviewWrapper
                  previewUrl={prodPopupPreviewUrl}
                  onOpenNewTab={() =>
                    prodPopupPreviewUrl && window.open(prodPopupPreviewUrl, '_blank')
                  }
                  onEdit={() => handleEditPage('production')}
                >
                  {prodPopupPreviewUrl && (
                    <iframe src={prodPopupPreviewUrl} className="w-full h-full" />
                  )}
                </BrowserPreviewWrapper>
              </div>
            </TabPanel>

            <TabPanel className="h-full">
              <div className="grid grid-cols-2 gap-4 h-full">
                <div>
                  <BrowserPreviewWrapper
                    previewUrl={localConfirmationPreviewUrl}
                    onOpenNewTab={() =>
                      localConfirmationPreviewUrl &&
                      window.open(localConfirmationPreviewUrl, '_blank')
                    }
                    onEdit={() => handleEditPage('local')}
                  >
                    {localConfirmationPreviewUrl && (
                      <iframe src={localConfirmationPreviewUrl} className="w-full h-full" />
                    )}
                  </BrowserPreviewWrapper>
                  <div className="mt-4">
                    <div>
                      Seller ID: <code>{currentPage.seller._id}</code>
                    </div>
                    <div>
                      Page ID: <code>{currentPage._id}</code>
                    </div>
                  </div>
                </div>

                <BrowserPreviewWrapper
                  previewUrl={prodConfirmationPreviewUrl}
                  onOpenNewTab={() =>
                    prodConfirmationPreviewUrl && window.open(prodConfirmationPreviewUrl, '_blank')
                  }
                  onEdit={() => handleEditPage('production')}
                >
                  {prodConfirmationPreviewUrl && (
                    <iframe src={prodConfirmationPreviewUrl} className="w-full h-full" />
                  )}
                </BrowserPreviewWrapper>
              </div>
            </TabPanel>

            <TabPanel className="h-full">
              <div className="grid grid-cols-2 gap-4 h-full">
                <div>
                  <BrowserPreviewWrapper
                    previewUrl={localEmailPreviewUrl}
                    onOpenNewTab={() =>
                      localEmailPreviewUrl && window.open(localEmailPreviewUrl, '_blank')
                    }
                    onEdit={() => handleEditPage('local')}
                  >
                    {currentPage?._id && (
                      <iframe
                        src={localEmailPreviewUrl}
                        className={clsx(
                          'origin-top-left',
                          'scale-50 w-[200%] h-[calc(200%)]',
                          'xl:scale-75 xl:w-[133.33%] xl:h-[133.33%]',
                          '3xl:scale-100 3xl:w-full 3xl:h-full',
                        )}
                      />
                    )}
                  </BrowserPreviewWrapper>
                  <div className="mt-4">
                    <div>
                      Seller ID: <code>{currentPage.seller._id}</code>
                    </div>
                    <div>
                      Page ID: <code>{currentPage._id}</code>
                    </div>
                  </div>
                </div>
                <BrowserPreviewWrapper
                  previewUrl={prodEmailPreviewUrl}
                  onOpenNewTab={() =>
                    prodEmailPreviewUrl && window.open(prodEmailPreviewUrl, '_blank')
                  }
                  onEdit={() => handleEditPage('production')}
                >
                  {currentPage?._id && (
                    <iframe
                      src={prodEmailPreviewUrl}
                      className={clsx(
                        'origin-top-left',
                        'scale-50 w-[200%] h-[calc(200%)]',
                        'xl:scale-75 xl:w-[133.33%] xl:h-[133.33%]',
                        '3xl:scale-100 3xl:w-full 3xl:h-full',
                      )}
                    />
                  )}
                </BrowserPreviewWrapper>
              </div>
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </div>
    </div>
  );
}
